import React, { useState, useEffect, useRef } from "react";
import { type ActivityRequest, ActivityCategory } from "../types";
import { XIcon, ImageIcon, CameraIcon } from "./icons/FeatherIcons";
import LocationAutocomplete from "./LocationAutocomplete";
import ActivityImageSelectionModal from "./ActivityImageSelectionModal";
import ConfirmModal from "./ConfirmModal";
import {
  loadGoogleMapsApi,
  getPlaceDetails,
} from "../services/locationService";
import { getActivityImageUrl } from "../services/imageService";
import { useToast } from "../contexts/ToastContext";
import { CATEGORIES } from "../constants";
import { usePageTracking } from "../hooks/usePageTracking";
import { createPortal } from "react-dom";
import { crashlytics } from "../services/firebase";
import { fallbackImage } from "../utils/getCachedImage";
import { toJsDate } from "../utils/toJsDate";
import { useCachedImage } from "../hooks/useCachedImage";

interface EditActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  activity: ActivityRequest;
  onSave: (activity: ActivityRequest) => void;
}

interface PortalProps {
  children: React.ReactNode;
}

const Portal: React.FC<PortalProps> = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return mounted ? createPortal(children, document.body) : null;
};

const EditActivityModal: React.FC<EditActivityModalProps> = ({
  isOpen,
  onClose,
  activity,
  onSave,
}) => {
  usePageTracking("EditActivity", "EditActivityModal");
  const [formData, setFormData] = useState<
    ActivityRequest & { groupSize: number | string }
  >(activity);
  const [hours, setHours] = useState("1");
  const [minutes, setMinutes] = useState("0");
  const [inputGroupSize, setInputGroupSize] = useState<string>("");
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [keyboardClosing, setKeyboardClosing] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const [isApiLoaded, setIsApiLoaded] = useState(false);
  const [isLocationVerified, setIsLocationVerified] = useState(true);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(
    activity.imageUrl || "",
  );
  const { addToast } = useToast();
  const [isPrivacyConfirmOpen, setIsPrivacyConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Cache the selected activity image
  const cachedImageUrl = useCachedImage(
    selectedImageUrl,
    activity.title || "activity",
    activity.id,
  );

  const getDayLabel = (isoDate: string) => {
    const date = toJsDate(isoDate);
    if (!date || isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  useEffect(() => {
    if (isOpen) {
      loadGoogleMapsApi(["places"])
        .then(() => setIsApiLoaded(true))
        .catch((err) => {
          setIsApiLoaded(true);
          crashlytics.logError(
            `Error loading Google Maps API for edit modal. Error: ${JSON.stringify(err?.message)}`,
            "EditActivityModal.tsx",
            err,
          );
          addToast("Location search is unavailable.", "error");
        });
    }
  }, [isOpen, addToast]);

  // Handle keyboard visibility detection on mobile
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      // Detect if keyboard is likely open based on viewport height change
      const viewportHeight =
        window.visualViewport?.height || window.innerHeight;
      const screenHeight = screen.height;
      const threshold = screenHeight * 0.75; // If viewport is less than 75% of screen, keyboard is likely open

      const keyboardOpen = viewportHeight < threshold;

      if (isKeyboardOpen && !keyboardOpen) {
        // Keyboard is closing
        setKeyboardClosing(true);
        setTimeout(() => setKeyboardClosing(false), 300); // 300ms delay to prevent accidental clicks
      }

      setIsKeyboardOpen(keyboardOpen);
    };

    // Use visualViewport if available (better for mobile), fallback to window resize
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleResize);
    } else {
      window.addEventListener("resize", handleResize);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleResize);
      } else {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, [isOpen, isKeyboardOpen]);

  useEffect(() => {
    setFormData(activity);
    setSelectedImageUrl(activity.imageUrl || "");
    setInputGroupSize(String(activity.groupSize || ""));
    setIsLocationVerified(true); // Always assume the initial location is verified
    setIsPrivacyConfirmOpen(false);
    if (activity.duration) {
      setHours(String(Math.floor(activity.duration / 60)));
      setMinutes(String(activity.duration % 60));
    } else {
      setHours("1");
      setMinutes("0");
    }
  }, [activity]);

  // Auto-fetch image if none exists
  useEffect(() => {
    if (formData.category && formData.title && !selectedImageUrl) {
      getActivityImageUrl(
        formData.category as ActivityCategory,
        formData.title,
        formData.description || "",
      )
        .then((url) => {
          setSelectedImageUrl(url);
          setFormData((prev) => ({ ...prev, imageUrl: url }));
        })
        .catch((error) => {
          // Failed to get default image, using fallback
        });
    }
  }, [
    formData.category,
    formData.title,
    formData.description,
    selectedImageUrl,
  ]);

  const handleImageSelect = (imageUrl: string) => {
    setSelectedImageUrl(imageUrl);
    setFormData((prev) => ({ ...prev, imageUrl }));
    setShowImageModal(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    const modal = modalRef.current;
    if (!modal) return;

    const focusableElements = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // 1. Initial Focus: Only run this once!
    // We check if the active element is already inside the modal
    if (!modal.contains(document.activeElement)) {
      firstElement?.focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;

      if (e.shiftKey) {
        // Shift+Tab
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    const target = e.target as HTMLInputElement;
    setFormData((prev) => ({
      ...prev,
      [name]: target.type === "number" ? Number(value) : value,
    }));
  };

  const handleCategoryChange = (category: ActivityCategory) => {
    setFormData((prev) => ({ ...prev, category }));
  };

  const handlePrivacyToggle = () => {
    if (formData.isPrivate) {
      setIsPrivacyConfirmOpen(true);
      return;
    }

    setFormData((prev) => ({ ...prev, isPrivate: true }));
  };

  const handleDateTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const localDateTime = e.target.value;
    const isoDateTime = new Date(localDateTime).toISOString();
    setFormData((prev) => ({ ...prev, dateTime: isoDateTime }));
  };

  const handleLocationInputChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      location: value,
      coordinates: undefined,
    }));
    setIsLocationVerified(false); // Any manual typing invalidates the location until re-selected
  };

  const handleLocationSelect = async (place: {
    description: string;
    placeId: string;
  }) => {
    addToast("Verifying location...", "info");
    try {
      const details = await getPlaceDetails(place.placeId);
      setFormData((prev) => ({
        ...prev,
        location: details.formattedAddress || place.description,
        coordinates: details.coordinates,
      }));
      setIsLocationVerified(true);
      addToast("Location updated!", "success");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Could not retrieve details for that location.";
      addToast(message, "error");
      setIsLocationVerified(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (keyboardClosing || isSaving) return;

    const selectedDateTime = new Date(formData.dateTime);
    if (selectedDateTime < new Date()) {
      addToast(
        "You can't schedule an activity in the past. Please choose a future date and time.",
        "error",
      );
      return;
    }

    if (!isLocationVerified || !formData.coordinates) {
      addToast("Please select a valid location from the suggestions.", "error");
      return;
    }

    const totalDuration =
      (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0);
    if (totalDuration < 15) {
      addToast("Duration must be at least 15 minutes.", "error");
      return;
    }

    const minGroupSize = Math.max(2, activity.participants.length || 1);
    const groupSizeNum =
      typeof formData.groupSize === "string"
        ? parseInt(formData.groupSize) || 0
        : formData.groupSize || 0;
    if (groupSizeNum < minGroupSize) {
      addToast(
        `Group size must be at least ${minGroupSize} people (current participants: ${activity.participants.length || 0}).`,
        "error",
      );
      return;
    }

    const finalFormData = {
      ...formData,
      duration: totalDuration,
      groupSize:
        typeof formData.groupSize === "string"
          ? parseInt(formData.groupSize)
          : formData.groupSize,
    };

    try {
      setIsSaving(true);
      await onSave(finalFormData);
    } catch (err) {
      addToast("Could not save changes. Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const getLocalDateTimeForInput = (isoDate: string) => {
    try {
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) {
        // Return a default value or handle error if date is invalid
        const now = new Date();
        now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
        return now.toISOString().slice(0, 16);
      }
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (e) {
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      return now.toISOString().slice(0, 16);
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div
        className="fixed inset-0 bg-black/70 flex items-center justify-center z-[110]"
        onClick={(e) => {
          // Block backdrop-closing while the privacy confirmation is showing
          if (isPrivacyConfirmOpen || isSaving) {
            e.stopPropagation();
            return;
          }
          onClose();
        }}
      >
        <div
          ref={modalRef}
          className="relative bg-white rounded-2xl w-full max-w-lg m-4 p-6 flex flex-col max-h-[90vh]"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-activity-title"
          aria-busy={isSaving}
        >
          {isSaving && (
            <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] z-20 flex flex-col items-center justify-center gap-2 text-gray-700">
              <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" aria-hidden="true" />
              <span className="text-sm font-medium">Saving changes…</span>
            </div>
          )}
          <div className="flex justify-between items-center mb-6">
            <h2
              id="edit-activity-title"
              className="text-2xl font-bold text-gray-900"
            >
              Edit Activity
            </h2>
            <button
              onClick={isSaving ? undefined : onClose}
              aria-label="Close modal"
              className="text-gray-500 hover:text-gray-900 disabled:opacity-50"
              disabled={isSaving}
            >
              <XIcon className="w-7 h-7" />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto pr-2 -mr-4 space-y-5 hide-scrollbar"
          >
            {/* 1. Activity Title */}
            <div>
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-600 mb-1"
              >
                Activity Title
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Evening Coffee & Chat"
                className="w-full box-border bg-gray-100 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary focus:ring-offset-0 focus:border-primary text-gray-900 border border-gray-300"
                maxLength={100}
              />
              <p className="text-right text-xs text-gray-400 mt-1">
                {formData.title.length}/100
              </p>
            </div>

            {/* 2. Description */}
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-600 mb-1"
              >
                Description (Optional)
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description || ""}
                onChange={handleChange}
                rows={3}
                placeholder="Tell us a bit more about the plan..."
                className="w-full box-border bg-gray-100 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary focus:ring-offset-0 focus:border-primary text-gray-900 border border-gray-300"
                maxLength={500}
              />
              <p className="text-right text-xs text-gray-400 mt-1">
                {(formData.description || "").length}/500
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Supports rich text: *bold*, _italic_, ~strike~, `code`, and links.
              </p>
            </div>

            {/* 3. Category Selection */}
            <div>
              <label
                htmlFor="category"
                className="block text-sm font-medium text-gray-600 mb-1"
              >
                Category
              </label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) =>
                  handleCategoryChange(e.target.value as ActivityCategory)
                }
                className="w-full box-border bg-gray-100 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary focus:ring-offset-0 focus:border-primary text-gray-900 border border-gray-300 appearance-none"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* 4. Date & Time */}
            <div>
              <label
                htmlFor="dateTime"
                className="block text-sm font-medium text-gray-600 mb-1"
              >
                Date & Time *
              </label>
              <input
                type="datetime-local"
                id="dateTime"
                name="dateTime"
                value={getLocalDateTimeForInput(formData.dateTime)}
                onChange={handleDateTimeChange}
                className="w-full box-border bg-gray-100 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary focus:ring-offset-0 focus:border-primary text-gray-900 border border-gray-300"
              />
              {getDayLabel(formData.dateTime) && (
                <p className="text-xs text-gray-500 mt-1">
                  Day: {getDayLabel(formData.dateTime)}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Select when you'd like this activity to take place (must be in
                the future)
              </p>
            </div>

            {/* 5. Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Duration
              </label>

              {/* Quick Duration Presets */}
              <div className="flex flex-wrap gap-2 mb-3">
                {[
                  { label: "30min", hours: "0", minutes: "30" },
                  { label: "1hr", hours: "1", minutes: "0" },
                  { label: "1.5hrs", hours: "1", minutes: "30" },
                  { label: "2hrs", hours: "2", minutes: "0" },
                  { label: "3hrs", hours: "3", minutes: "0" },
                ].map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => {
                      setHours(preset.hours);
                      setMinutes(preset.minutes);
                    }}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      hours === preset.hours && minutes === preset.minutes
                        ? "bg-primary text-white border-primary"
                        : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center space-x-2">
                <div className="relative flex-1">
                  <input
                    id="hours"
                    type="number"
                    min="0"
                    max="12"
                    value={hours}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty string, then validate and constrain
                      if (value === "") {
                        setHours("");
                      } else {
                        const numValue = parseInt(value);
                        if (!isNaN(numValue)) {
                          setHours(String(Math.max(0, Math.min(12, numValue))));
                        }
                      }
                    }}
                    className="w-full box-border bg-gray-100 rounded-lg pr-12 pl-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary focus:ring-offset-0 focus:border-primary text-gray-900 border border-gray-300"
                    placeholder="1"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                    hr
                  </span>
                </div>
                <div className="relative flex-1">
                  <input
                    id="minutes"
                    type="number"
                    min="0"
                    max="59"
                    step="1"
                    value={minutes}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty string, then validate and constrain
                      if (value === "") {
                        setMinutes("");
                      } else {
                        const numValue = parseInt(value);
                        if (!isNaN(numValue)) {
                          // Allow any minute value from 0-59
                          setMinutes(
                            String(Math.max(0, Math.min(59, numValue))),
                          );
                        }
                      }
                    }}
                    className="w-full box-border bg-gray-100 rounded-lg pr-12 pl-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary focus:ring-offset-0 focus:border-primary text-gray-900 border border-gray-300"
                    placeholder="0"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                    min
                  </span>
                </div>
              </div>

              {/* Show total duration with better formatting */}
              {(() => {
                const totalMinutes =
                  (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0);
                if (totalMinutes === 0) {
                  return (
                    <p className="text-xs text-gray-400 mt-1">
                      Enter a duration for your activity
                    </p>
                  );
                }
                if (totalMinutes < 15) {
                  return (
                    <p className="text-xs text-red-500 mt-1">
                      ⚠️ Minimum duration is 15 minutes
                    </p>
                  );
                }
                const displayHours = Math.floor(totalMinutes / 60);
                const displayMins = totalMinutes % 60;
                let display = "";
                if (displayHours > 0) display += `${displayHours}h `;
                if (displayMins > 0) display += `${displayMins}m`;
                return (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Duration: {display.trim()}
                  </p>
                );
              })()}
            </div>

            {/* 6. Location */}
            <div>
              {isApiLoaded ? (
                <LocationAutocomplete
                  id="location"
                  label="Location"
                  initialValue={formData.location}
                  onLocationSelect={handleLocationSelect}
                  onInputChange={handleLocationInputChange}
                />
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    placeholder="Loading location search..."
                    className="w-full bg-gray-200 rounded-lg px-4 py-2.5 cursor-wait"
                    disabled
                  />
                </div>
              )}
              {!isLocationVerified && (
                <p className="text-xs text-yellow-600 mt-1">
                  Please select a location from the list to verify.
                </p>
              )}
            </div>
            {/* 7. Group Size */}
            <div>
              <label
                htmlFor="groupSize"
                className="block text-sm font-medium text-gray-600 mb-1"
              >
                Group Size
              </label>

              {/* Quick Group Size Presets */}
              <div className="flex flex-wrap gap-2 mb-3">
                {[
                  { label: "2 people", value: "2" },
                  { label: "3-4 people", value: "4" },
                  { label: "5-6 people", value: "6" },
                  { label: "8-10 people", value: "10" },
                  { label: "15+ people", value: "15" },
                ].map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => {
                      setInputGroupSize(preset.value);
                      setFormData((prev) => ({
                        ...prev,
                        groupSize: parseInt(preset.value),
                      }));
                    }}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      inputGroupSize === preset.value
                        ? "bg-primary text-white border-primary"
                        : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              <input
                id="groupSize"
                type="number"
                min={Math.max(2, activity.participants.length || 1)}
                max="50"
                step="1"
                value={inputGroupSize}
                key="groupSize-input"
                onFocus={(e) => {
                  // Select all text when user focuses on the input for easy editing
                  e.target.select();
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  // Update the input-specific state immediately
                  setInputGroupSize(value);
                  // Also update formData for validation display
                  setFormData((prev) => ({
                    ...prev,
                    groupSize: value === "" ? 0 : parseInt(value),
                  }));
                }}
                onBlur={(e) => {
                  // Ensure minimum value when user leaves the field
                  const value = parseInt(e.target.value);
                  const minRequired = Math.max(
                    2,
                    activity.participants.length || 1,
                  );
                  if (isNaN(value) || value < minRequired) {
                    const correctedValue = minRequired;
                    setInputGroupSize(String(correctedValue));
                    setFormData((prev) => ({
                      ...prev,
                      groupSize: correctedValue,
                    }));
                  }
                }}
                className="w-full box-border bg-gray-100 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary focus:ring-offset-0 focus:border-primary text-gray-900 border border-gray-300"
                placeholder="2"
                required
              />
              {(() => {
                const size =
                  typeof formData.groupSize === "string"
                    ? parseInt(formData.groupSize) || 0
                    : formData.groupSize || 0;
                const minRequired = Math.max(
                  2,
                  activity.participants.length || 1,
                );
                if (size === 0 || String(formData.groupSize) === "") {
                  return (
                    <p className="text-xs text-gray-400 mt-1">
                      Enter number of people joining
                    </p>
                  );
                }
                if (size < minRequired) {
                  return (
                    <p className="text-xs text-red-500 mt-1">
                      ⚠️ Minimum {minRequired} people required (current
                      participants: {activity.participants.length || 0})
                    </p>
                  );
                }
                return (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Group size: {size} people
                  </p>
                );
              })()}
            </div>

            {/* 8. Privacy */}
            <div>
              <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900">
                      Private Activity
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.isPrivate
                      ? "Only visible via link. You can invite specific users."
                      : "Visible to everyone on home feed and map."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handlePrivacyToggle}
                  className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                    formData.isPrivate ? "bg-primary" : "bg-gray-300"
                  }`}
                  aria-pressed={Boolean(formData.isPrivate)}
                  aria-label="Toggle private activity"
                >
                  <span
                    className={`inline-block w-4 h-4 transform transition-transform bg-white rounded-full ${
                      formData.isPrivate ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* 9. Activity Photo */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Activity Photo
              </label>
              <div className="flex items-center space-x-3">
                {selectedImageUrl && (
                  <div className="w-20 h-24 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={cachedImageUrl}
                      alt="Selected activity photo"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        if (e.currentTarget.src !== fallbackImage) {
                          e.currentTarget.src = fallbackImage;
                        }
                      }}
                    />
                  </div>
                )}
                <div className="flex-1">
                  <button
                    type="button"
                    onClick={() => setShowImageModal(true)}
                    className="w-full bg-gray-100 text-gray-700 font-medium py-2.5 px-4 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2 border border-gray-300"
                  >
                    <CameraIcon className="w-4 h-4" />
                    <span>
                      {selectedImageUrl ? "Change Photo" : "Choose Photo"}
                    </span>
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    We'll automatically suggest relevant photos based on your
                    activity
                  </p>
                </div>
              </div>
            </div>
          </form>

          <div
            className={`pt-2 transition-all duration-200 ${isKeyboardOpen ? "pb-4" : ""}`}
          >
            <button
              type="submit"
              onClick={(e) => {
                // Only handle click if it's not from a touch event
                if (e.detail !== 0) {
                  handleSubmit(e);
                }
              }}
              onTouchEnd={(e) => {
                // Prevent default to avoid double-tap issues on mobile
                if (keyboardClosing) {
                  e.preventDefault();
                  return;
                }
                // Add small delay on touch to ensure keyboard is fully closed
                setTimeout(() => {
                  if (!keyboardClosing) {
                    handleSubmit(e as any);
                  }
                }, 50);
              }}
              disabled={
                !isApiLoaded || !isLocationVerified || keyboardClosing || isSaving
              }
              className={`w-full bg-primary text-white font-bold py-3 px-4 rounded-full disabled:opacity-50 transition-all duration-300 shadow-lg shadow-primary/30 ${
                keyboardClosing || isSaving
                  ? "pointer-events-none"
                  : "hover:opacity-90 transform hover:scale-105"
              } ${isKeyboardOpen ? "sticky bottom-4 z-10" : ""}`}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        {/* Image Selection Modal */}
        <ActivityImageSelectionModal
          isOpen={showImageModal}
          onClose={() => setShowImageModal(false)}
          onSelect={handleImageSelect}
          category={formData.category as ActivityCategory}
          title={formData.title}
          description={formData.description || undefined}
          location={formData.location}
          currentImageUrl={selectedImageUrl}
        />
        <ConfirmModal
          isOpen={isPrivacyConfirmOpen}
          title="Make activity public?"
          message="This will make the activity visible on the home feed and map to everyone. You can invite users, but it will no longer be private."
          confirmText="Make Public"
          confirmStyle="danger"
          onConfirm={() => {
            setFormData((prev) => ({ ...prev, isPrivate: false }));
            setIsPrivacyConfirmOpen(false);
          }}
          onCancel={() => setIsPrivacyConfirmOpen(false)}
        />
      </div>
    </Portal>
  );
};

export default EditActivityModal;
